import { useState } from 'react';
import { 
  Palette, 
  Type, 
  Layout, 
  Check, 
  ChevronRight,
  Sparkles,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  COLOR_SCHEMES, 
  TYPOGRAPHY_PRESETS, 
  LANDING_TEMPLATES,
  getGoogleFontsUrl 
} from '@/lib/landingDesignSystem';

// ==================== COLOR SCHEME SELECTOR ====================
export const ColorSchemeSelector = ({ selected, onSelect, compact = false }) => {
  const schemes = Object.values(COLOR_SCHEMES);
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {schemes.map((scheme) => (
          <button
            key={scheme.id}
            onClick={() => onSelect(scheme.id)}
            className={`group relative w-8 h-8 rounded-full overflow-hidden transition-all hover:scale-110 ${
              selected === scheme.id ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''
            }`}
            title={scheme.name}
          >
            <div 
              className="absolute inset-0"
              style={{ background: scheme.gradient }}
            />
            {selected === scheme.id && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 gap-3">
      {schemes.map((scheme) => (
        <button
          key={scheme.id}
          onClick={() => onSelect(scheme.id)}
          className={`group relative p-4 rounded-xl border-2 transition-all text-left ${
            selected === scheme.id 
              ? 'border-brand-500 bg-brand/10' 
              : 'border-border-subtle hover:border-brand/50'
          }`}
        >
          {/* Color Preview */}
          <div className="flex gap-1 mb-3">
            <div 
              className="w-6 h-6 rounded-full shadow-inner"
              style={{ backgroundColor: scheme.primary }}
            />
            <div 
              className="w-6 h-6 rounded-full shadow-inner"
              style={{ backgroundColor: scheme.secondary }}
            />
            <div 
              className="w-6 h-6 rounded-full shadow-inner"
              style={{ backgroundColor: scheme.accent }}
            />
          </div>
          
          {/* Info */}
          <p className="font-medium text-text-main text-sm">{scheme.name}</p>
          <p className="text-xs text-text-muted">{scheme.description}</p>
          
          {/* Selected indicator */}
          {selected === scheme.id && (
            <div className="absolute top-2 right-2">
              <Check className="w-4 h-4 text-brand-500" />
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

// ==================== TYPOGRAPHY SELECTOR ====================
export const TypographySelector = ({ selected, onSelect }) => {
  const presets = Object.values(TYPOGRAPHY_PRESETS);
  
  return (
    <div className="space-y-2">
      {presets.map((preset) => (
        <button
          key={preset.id}
          onClick={() => onSelect(preset.id)}
          className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
            selected === preset.id 
              ? 'border-brand-500 bg-brand/10' 
              : 'border-border-subtle hover:border-brand/50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p 
                className="text-lg text-text-main"
                style={{ 
                  fontFamily: preset.headingFont,
                  fontWeight: preset.headingWeight 
                }}
              >
                {preset.name}
              </p>
              <p 
                className="text-sm text-text-muted mt-1"
                style={{ fontFamily: preset.bodyFont }}
              >
                Texto de ejemplo para ver la fuente
              </p>
            </div>
            {selected === preset.id && (
              <Check className="w-5 h-5 text-brand-500 flex-shrink-0" />
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

// ==================== TEMPLATE SELECTOR ====================
export const TemplateSelector = ({ selected, onSelect, onPreview }) => {
  const templates = Object.values(LANDING_TEMPLATES);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {templates.map((template) => {
        const colorScheme = COLOR_SCHEMES[template.colorScheme];
        
        return (
          <div
            key={template.id}
            className={`group relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer ${
              selected === template.id 
                ? 'border-brand-500 ring-2 ring-brand/20' 
                : 'border-border-subtle hover:border-brand/50'
            }`}
            onClick={() => onSelect(template.id)}
          >
            {/* Template Preview */}
            <div 
              className="aspect-[4/3] relative"
              style={{ background: colorScheme?.heroGradient || colorScheme?.gradient }}
            >
              {/* Placeholder mockup */}
              <div className="absolute inset-4 flex flex-col">
                {/* Mini hero */}
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                  <div 
                    className="w-3/4 h-3 rounded mb-2"
                    style={{ backgroundColor: colorScheme?.text, opacity: 0.9 }}
                  />
                  <div 
                    className="w-1/2 h-2 rounded mb-4"
                    style={{ backgroundColor: colorScheme?.text, opacity: 0.5 }}
                  />
                  <div 
                    className="w-24 h-6 rounded-lg"
                    style={{ background: colorScheme?.gradient }}
                  />
                </div>
                
                {/* Section indicators */}
                <div className="flex justify-center gap-1 pb-2">
                  {template.sections.slice(0, 5).map((_, idx) => (
                    <div 
                      key={idx}
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: colorScheme?.primary, opacity: 0.5 }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button 
                  size="sm" 
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview?.(template.id);
                  }}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview
                </Button>
              </div>
              
              {/* Selected badge */}
              {selected === template.id && (
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-500 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            
            {/* Template Info */}
            <div className="p-4 bg-surface">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-text-main">{template.name}</h3>
                <span className="text-xs text-text-muted">{template.sections.length} secciones</span>
              </div>
              <p className="text-sm text-text-muted">{template.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== DESIGN SETTINGS PANEL ====================
export const DesignSettingsPanel = ({ 
  colorScheme, 
  typography, 
  template,
  onColorSchemeChange, 
  onTypographyChange,
  onTemplateChange,
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState('colors');
  
  const tabs = [
    { id: 'colors', label: 'Colores', icon: Palette },
    { id: 'typography', label: 'Tipografía', icon: Type },
    { id: 'templates', label: 'Plantillas', icon: Layout }
  ];
  
  return (
    <div className="bg-surface border border-border-subtle rounded-2xl shadow-2xl overflow-hidden w-[420px]">
      {/* Header */}
      <div className="px-4 py-3 bg-app-bg/50 border-b border-border-subtle flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-brand-500" />
          <span className="font-semibold text-text-main">Diseño de Landing</span>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 hover:bg-surface rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-text-muted" />
        </button>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-border-subtle">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'text-brand-500 border-b-2 border-brand-500 bg-brand/5' 
                : 'text-text-muted hover:text-text-main'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="p-4 max-h-[60vh] overflow-y-auto">
        {activeTab === 'colors' && (
          <ColorSchemeSelector 
            selected={colorScheme} 
            onSelect={onColorSchemeChange} 
          />
        )}
        
        {activeTab === 'typography' && (
          <>
            {/* Load Google Fonts preview */}
            <link 
              href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800&family=Playfair+Display:wght@400;700&family=Bebas+Neue&family=Space+Grotesk:wght@400;700&family=Nunito:wght@400;800&display=swap" 
              rel="stylesheet"
            />
            <TypographySelector 
              selected={typography} 
              onSelect={onTypographyChange} 
            />
          </>
        )}
        
        {activeTab === 'templates' && (
          <TemplateSelector 
            selected={template} 
            onSelect={onTemplateChange}
          />
        )}
      </div>
    </div>
  );
};

export default DesignSettingsPanel;
